import { useEffect, useState } from 'react';
import {
  MIN_PLAYERS_TO_START,
  ROUNDS_TO_WIN,
  ROUND_DURATION_MS,
  WEAPONS,
  WEAPON_ORDER,
  type WeaponId,
} from '@game/shared';
import { cn } from '../lib/cn.js';
import { useGameStore, type LobbyRow } from '../game/store.js';
import { Button } from './button.jsx';

/**
 * Re-renders once a second so the countdown ticks. The deadline itself lives in
 * the store as an absolute time, so this never needs a message from the server
 * to stay accurate — and a dropped packet cannot stall the clock.
 */
const useCountdown = (endsAtMs: number): number => {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (endsAtMs === 0) {
      setRemaining(0);
      return;
    }
    const update = () => setRemaining(Math.max(0, endsAtMs - performance.now()));
    update();
    const timer = window.setInterval(update, 200);
    return () => window.clearInterval(timer);
  }, [endsAtMs]);
  return remaining;
};

const WeaponCard = ({
  id,
  selected,
  onPick,
}: {
  id: WeaponId;
  selected: boolean;
  onPick: (id: WeaponId) => void;
}) => {
  const def = WEAPONS[id];
  const stats: [string, string][] = [
    ['DMG', String(def.damage)],
    ['RPM', String(def.rpm)],
    ['MAG', String(def.magazine)],
  ];
  return (
    <button
      onClick={() => onPick(id)}
      className={cn(
        'rounded-lg border p-4 text-left transition-colors',
        selected
          ? 'border-team-blue bg-team-blue/15'
          : 'border-white/10 bg-white/5 hover:border-white/25 hover:bg-white/10',
      )}
    >
      <div className="flex items-baseline justify-between">
        <span className="font-bold tracking-wide">{def.name}</span>
        {selected && <span className="text-xs text-team-blue">SELECTED</span>}
      </div>
      <div className="mt-3 flex gap-4 tabular">
        {stats.map(([label, value]) => (
          <div key={label}>
            <div className="text-[10px] tracking-[0.2em] text-ink-muted">{label}</div>
            <div className="text-lg font-semibold">{value}</div>
          </div>
        ))}
      </div>
      {def.pellets > 1 && (
        <div className="mt-2 text-xs text-ink-muted">{def.pellets} pellets per shot</div>
      )}
    </button>
  );
};

const TeamColumn = ({ team, rows }: { team: 0 | 1; rows: LobbyRow[] }) => (
  <div>
    <div
      className={cn(
        'text-[11px] uppercase tracking-[0.2em]',
        team === 0 ? 'text-team-blue' : 'text-team-red',
      )}
    >
      {team === 0 ? 'Blue' : 'Red'} · {rows.length}
    </div>
    <ul className="mt-2 space-y-1">
      {rows.map((row) => (
        <li key={row.id} className="flex items-center justify-between gap-3 text-sm">
          <span className={cn('truncate', row.isSelf && 'font-semibold')}>
            {row.name}
            {row.isSelf && <span className="ml-1.5 text-ink-muted">(you)</span>}
          </span>
          <span className={row.ready ? 'text-health' : 'text-ink-muted'}>
            {row.ready ? '✓' : '…'}
          </span>
        </li>
      ))}
      {rows.length === 0 && <li className="text-sm text-ink-muted">Empty</li>}
    </ul>
  </div>
);

export const Lobby = ({
  onSelect,
  onSettings,
  onLeave,
}: {
  onSelect: (weapon: WeaponId) => void;
  onSettings: () => void;
  onLeave: () => void;
}) => {
  const matchPhase = useGameStore((state) => state.matchPhase);
  const phaseEndsAtMs = useGameStore((state) => state.phaseEndsAtMs);
  const mapName = useGameStore((state) => state.mapName);
  const mapTagline = useGameStore((state) => state.mapTagline);
  const lobby = useGameStore((state) => state.lobby);
  const loadout = useGameStore((state) => state.loadout);

  const remainingMs = useCountdown(phaseEndsAtMs);
  const seconds = Math.ceil(remainingMs / 1000);

  const blue = lobby.filter((row) => row.team === 0);
  const red = lobby.filter((row) => row.team === 1);
  const readyCount = lobby.filter((row) => row.ready).length;
  const needed = Math.max(0, MIN_PLAYERS_TO_START - lobby.length);

  const headline =
    matchPhase === 'waiting'
      ? `Waiting for ${needed} more player${needed === 1 ? '' : 's'}`
      : 'Choose your loadout';

  return (
    <div className="fixed inset-0 z-10 grid place-items-center bg-surface-900/75 backdrop-blur-sm">
      <div className="w-[min(760px,92vw)] animate-rise rounded-xl border border-white/10 bg-surface-800/90 p-8">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-ink-muted">
              {mapName}
            </div>
            <h2 className="mt-1 text-3xl font-extrabold tracking-tight">{headline}</h2>
            <p className="mt-1 text-sm text-ink-muted">{mapTagline}</p>
          </div>
          {phaseEndsAtMs > 0 && (
            <div className="text-right tabular">
              <div className="text-5xl font-bold leading-none">{seconds}</div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-ink-muted">
                Starts in
              </div>
            </div>
          )}
        </div>

        <div className="mt-7 grid grid-cols-2 gap-3">
          {WEAPON_ORDER.map((id) => (
            <WeaponCard
              key={id}
              id={id}
              selected={loadout === id}
              onPick={onSelect}
            />
          ))}
        </div>

        <div className="mt-7 grid grid-cols-2 gap-8 border-t border-white/10 pt-5">
          <TeamColumn team={0} rows={blue} />
          <TeamColumn team={1} rows={red} />
        </div>

        <div className="mt-5 flex items-end justify-between gap-6">
          <p className="text-sm text-ink-muted">
            {matchPhase === 'waiting'
              ? 'Nobody plays alone — the lobby opens once there are enough players.'
              : loadout
                ? `Locked in. ${readyCount}/${lobby.length} ready — the round starts as soon as everyone has, or when the timer runs out.`
                : `No respawns — wipe the other team to take the round. If both sides last the full ${Math.round(ROUND_DURATION_MS / 1000)}s, the higher total health wins it. First to ${ROUNDS_TO_WIN} takes the game.`}
          </p>
          <div className="flex shrink-0 gap-2">
            <Button variant="ghost" onClick={onSettings}>
              Settings
            </Button>
            <Button variant="ghost" onClick={onLeave}>
              Leave
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
